import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { Toaster } from "sonner";

import MainLayout from "./layouts/MainLayout";
import SubscriptionLayout from "./layouts/SubscriptionLayout";
import ClientDashboard from "./pages/client/Dashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AccessPage from "./pages/admin/access/AccessPage";
import EngagementBoard from "./pages/admin/EngagementBoard";
import AuditLogsPage from "./pages/admin/AuditLogsPage";
import ForumMonitoringPage, { ForumMonitoringDetailPage } from "./pages/admin/ForumMonitoringPage";
import "./styles/forum-monitoring.css";
import LeaksPage from "./pages/client/Leaks";
import SavedPosts from "./pages/client/SavedPosts";
import ExportHistory from "./pages/client/ExportHistory";
import LeakDetailPage from "./pages/client/LeakDetailPage";
import Contribute from "./pages/client/Contribute";
import AlertsPage from "./pages/client/AlertsPage";
import CustomerCheckPage from "./pages/client/CustomerCheckPage";
import TakedownPage from "./pages/client/TakedownPage";
import TimelinePage from "./pages/client/TimelinePage";
import DomainMonitorPage from "./pages/client/DomainMonitorPage";
import ReportsHubPage from "./pages/report-center/ReportsHubPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import ProfilePage from "./pages/profile/ProfilePage";
import FeedbackPage from "./pages/client/FeedbackPage";
import CouponPage from "./pages/coupon/CouponPage";
import HelpCenterPage from "./pages/help-center/HelpCenterPage";
import HelpCenterWikiPage from "./pages/help-center/HelpCenterWikiPage";
import HelpCenterContactPage from "./pages/help-center/HelpCenterContactPage";
import ApiAccessPage from "./pages/client/ApiAccessPage";
import Login from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import RequireAuth from "./auth/RequireAuth";
import FeatureGuard from "./components/FeatureGuard";

function RootRedirect() {
  const { user } = useAuth();
  const storedRole = localStorage.getItem("role");
  const role = (user?.role ?? storedRole ?? "").toLowerCase();

  if (!user && !storedRole) return <Navigate to="/login" replace />;
  if (role === "admin" || role === "super_admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/dashboard" replace />;
}

function ExternalDocsRedirect() {
  window.location.href = "https://threatactix.com/services/dark-web-monitoring/";
  return null;
}

export default function App() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
        <Route path="/docs" element={<ExternalDocsRedirect />} />

        <Route element={<RequireAuth><MainLayout /></RequireAuth>}>

          {/* Root Redirect */}
          <Route path="/" element={<RootRedirect />} />

          {/* Profile Routes (Available to all logged-in users) */}
          <Route path="/profile" element={<ProfilePage section="profile" />} />
          <Route path="/profile/security" element={<ProfilePage section="security" />} />
          <Route path="/profile/*" element={<Navigate to="/profile" replace />} />

          {/* Feedback Route (Available to all logged-in users) */}
          <Route path="/feedback" element={<FeedbackPage />} />

          {/* Coupon Route (Available to all logged-in users) */}
          <Route path="/coupon" element={<CouponPage />} />

          {/* Help Center Routes (Available to all logged-in users) */}
          <Route path="/help-center" element={<HelpCenterPage />} />
          <Route path="/help-center/wiki" element={<HelpCenterWikiPage />} />
          <Route path="/help-center/contact" element={<HelpCenterContactPage />} />

          {/* Core Feature Routes Protected by Dynamic Permissions */}
          <Route path="/dashboard" element={<FeatureGuard featureId="dashboard" featureName="Dashboard"><ClientDashboard /></FeatureGuard>} />
          <Route path="/leaks" element={<FeatureGuard featureId="intelligence_feed" featureName="Intelligence Feed"><LeaksPage /></FeatureGuard>} />
          <Route path="/leaks/saved" element={<FeatureGuard featureId="intelligence_feed" featureName="Intelligence Feed"><SavedPosts /></FeatureGuard>} />
          <Route path="/leaks/exports" element={<FeatureGuard featureId="intelligence_feed" featureName="Intelligence Feed"><ExportHistory /></FeatureGuard>} />
          <Route path="/leaks/:id" element={<FeatureGuard featureId="intelligence_feed" featureName="Intelligence Feed"><LeakDetailPage /></FeatureGuard>} />
          <Route path="/contribute" element={<FeatureGuard featureId="review" featureName="Review & Contribute"><Contribute /></FeatureGuard>} />
          <Route path="/alerts" element={<FeatureGuard featureId="alert_config" featureName="Alert Configuration"><AlertsPage /></FeatureGuard>} />
          <Route path="/customer-check" element={<FeatureGuard featureId="customer_check" featureName="Customer Check"><CustomerCheckPage /></FeatureGuard>} />
          <Route path="/takedowns" element={<FeatureGuard featureId="takedowns" featureName="Takedowns"><TakedownPage /></FeatureGuard>} />
          <Route path="/timeline" element={<FeatureGuard featureId="timeline" featureName="Timeline"><TimelinePage /></FeatureGuard>} />

          {/* Monitoring & Protection Routes */}
          <Route path="/domain-monitor" element={<FeatureGuard featureId="domain_monitor" featureName="Domain Monitor"><DomainMonitorPage /></FeatureGuard>} />
          <Route path="/reports" element={<FeatureGuard featureId="reports_export" featureName="Reports Center"><ReportsHubPage /></FeatureGuard>} />
          <Route path="/api-access" element={<ApiAccessPage />} />

          {/* Admin & Governance Routes */}
          <Route path="/admin" element={<FeatureGuard featureId="admin_dashboard" featureName="Admin Dashboard"><AdminDashboard /></FeatureGuard>} />
          <Route path="/access" element={<FeatureGuard featureId="access_management" featureName="Access Console"><AccessPage /></FeatureGuard>} />
          <Route path="/manage-clients" element={<Navigate to="/access" replace />} />

          <Route path="/engagement-board" element={<FeatureGuard featureId="engagement_board" featureName="Engagement Board"><EngagementBoard /></FeatureGuard>} />
          <Route path="/admin/audit" element={<FeatureGuard featureId="audit_logs" featureName="Audit Logs"><AuditLogsPage /></FeatureGuard>} />
          <Route path="/admin/forum-monitoring" element={<FeatureGuard featureId="forum_monitoring" featureName="Forum Monitoring"><ForumMonitoringPage /></FeatureGuard>} />
          <Route path="/admin/forum-monitoring/:forum" element={<FeatureGuard featureId="forum_monitoring" featureName="Forum Monitoring"><ForumMonitoringDetailPage /></FeatureGuard>} />
          <Route path="/admin/forum_monitoring" element={<Navigate to="/admin/forum-monitoring" replace />} />
          <Route path="/admin/forum_monitoring/:forum" element={<Navigate to="/admin/forum-monitoring" replace />} />

        </Route>

        <Route element={<RequireAuth><SubscriptionLayout /></RequireAuth>}>
          <Route path="/subscription" element={<FeatureGuard featureId="subscription" featureName="Subscription"><SubscriptionPage /></FeatureGuard>} />
        </Route>
      </Routes>
    </>
  );
}
